import React, { PropsWithChildren, useCallback, useContext, useRef } from 'react';

import { CustomBottomSheetModalProvider } from '@/feature/alerts/components/CustomBottomSheetModalProvider';

import {
    CurrencyOverviewModal,
    CurrencyOverviewModalRef,
    ShowCurrencyOverviewModalOptions,
} from './CurrencyOverviewModal';

interface CurrencyOverviewModalContextValue {
    showCurrencyOverviewModal: (options?: ShowCurrencyOverviewModalOptions) => void;
}

const CurrencyOverviewModalContext = React.createContext<CurrencyOverviewModalContextValue | undefined>(undefined);

/**
 * Provides access to the Modal used to display currency overview
 */
export const CurrencyOverviewModalProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const modalRef = useRef<CurrencyOverviewModalRef>(null);

    const showCurrencyOverviewModal = useCallback((options?: ShowCurrencyOverviewModalOptions) => {
        modalRef.current?.show(options);
    }, []);

    return (
        <CustomBottomSheetModalProvider>
            <CurrencyOverviewModalContext.Provider value={{ showCurrencyOverviewModal }}>
                {children}
                <CurrencyOverviewModal ref={modalRef} />
            </CurrencyOverviewModalContext.Provider>
        </CustomBottomSheetModalProvider>
    );
};

/**
 * Hook to display Currency Overview modal
 */
export const useCurrencyOverviewModal = () => {
    const context = useContext(CurrencyOverviewModalContext);
    if (context === undefined) {
        throw new Error('useCurrencyOverviewModal must be used within a CurrencyOverviewModalProvider');
    }
    return context;
};
